import React, { useState, useEffect } from "react";
import './charts.css';
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { BsGraphUp, BsPeopleFill, BsStarFill } from "react-icons/bs";
import axios from "axios";

const formatXAxisLabels = (label) => {
    if (!label || typeof label !== "string") return "";

    const college = label.toLowerCase().trim();

    if (college === "management") return "MANG";
    if (college === "aditya college of pharmacy" || college === "acop" || college === "acp") return "ACOP";
    if (college === "aditya polytechnic college" || college === "poly") return "Poly";
    if (college === "aditya college of engineering & technology" || college === "acet") return "ACET";
    if (college === "aditya university" || college === "aus") return "AUS";
    if (college === "aditya college of engineering") return "ACOE";
    if (college === "aditya pharmacy college") return "APC";
    if (college === "aditya college of polytechnic") return "ACPT";
    if (college === "aditya global business school") return "AGBS";
    if (college === "admin" || college === "administration" || college === "campus operations") return "Campus Operations";

    if (label.length > 8) {
        return label
            .trim()
            .split(/\s+/)
            .filter(word => word.length > 0)
            .map(word => word[0].toUpperCase())
            .join("");
    }

    return label;
};

const CustomTick = (props) => {
    const { x, y, payload } = props;
    const value = payload.value;
    const lowerValue = value ? value.toLowerCase().trim() : "";
    if (lowerValue === "campus operations" || lowerValue === "admin" || lowerValue === "administration") {
        return (
            <g transform={`translate(${x},${y})`}>
                <text x={0} y={0} dy={12} textAnchor="middle" fill="#6b7280" fontSize={11} fontWeight={500}>
                    <tspan x="0" dy="0">Campus</tspan>
                    <tspan x="0" dy="1.2em">Operations</tspan>
                </text>
            </g>
        );
    }
    return (
        <g transform={`translate(${x},${y})`}>
            <text x={0} y={0} dy={16} textAnchor="middle" fill="#6b7280" fontSize={11} fontWeight={500}>
                {formatXAxisLabels(value)}
            </text>
        </g>
    );
};

const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (
            <div className="custom-tooltip">
                <p className="tooltip-label">{formatXAxisLabels(label)}</p>
                <p className="tooltip-value">
                    <span className="tooltip-value-label">Number of Donors : </span>
                    <span className="tooltip-value-number">{payload[0].value}</span>
                </p>
            </div>
        );
    }
    return null;
};

function Chart1(props) {
    const [donorData, setDonorData] = useState([]);
    const port = process.env.REACT_APP_SERVER_PORT;

    useEffect(() => {
        const fetchData = async () => {
            try {
                const result = await axios.get(port + `count-by-department?collegeCode=${props.college}`);
                
                const actualResult = Object.entries(result.data).map(([key, value]) => ({
                    College: key,
                    "NUMBER OF DONORS": value
                }));

                if (actualResult.length === 1) {
                    actualResult.push({ College: "Others", "NUMBER OF DONORS": 0 });
                }
                setDonorData(actualResult);
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };

        fetchData();
    }, [props.college, port]);

    // Calculate metrics dynamically
    const totalColleges = donorData.filter(item => item.College !== "Others" || item["NUMBER OF DONORS"] > 0).length;
    const totalDonors = donorData.reduce((sum, item) => sum + (item["NUMBER OF DONORS"] || 0), 0);

    let highestCollege = "N/A";
    let highestCount = 0;
    if (donorData.length > 0) {
        const maxItem = donorData.reduce((prev, current) => 
            ((prev["NUMBER OF DONORS"] || 0) > (current["NUMBER OF DONORS"] || 0)) ? prev : current
        );
        highestCollege = formatXAxisLabels(maxItem.College);
        highestCount = maxItem["NUMBER OF DONORS"] || 0;
    }

    return (
        <div className="chart1">
            <div className="graph-card">
                {/* Custom Card Header */}
                <div className="graph-card-header">
                    <div className="graph-card-title-container">
                        <div className="graph-icon-wrapper">
                            <BsGraphUp className="graph-header-icon" />
                        </div>
                        <span className="graph-card-title">Number of Donors</span>
                    </div>
                    <div className="graph-card-legend">
                        <span className="legend-dot"></span>
                        <span className="legend-text">Number of Donors</span>
                    </div>
                </div>

                {/* Graph wrapper */}
                <div className="chart-wrapper">
                    <ResponsiveContainer height={300} width="100%">
                        <AreaChart data={donorData} margin={{ top: 10, right: 10, left: -20, bottom: 20 }}>
                            <defs>
                                <linearGradient id="colorDonors" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#ff0000" stopOpacity={0.3} />
                                    <stop offset="95%" stopColor="#ff0000" stopOpacity={0.0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eaeaea" />
                            <XAxis 
                                dataKey="College" 
                                tickLine={false}
                                axisLine={{ stroke: '#eaeaea' }}
                                tick={<CustomTick />}
                            />
                            <YAxis 
                                tickLine={false}
                                axisLine={false}
                                tick={{ fill: '#6b7280', fontSize: 11, fontWeight: 500 }}
                                label={{ value: 'Donors', angle: -90, position: 'insideLeft', offset: 0, style: { fill: '#6b7280', fontSize: 11, fontWeight: 500 } }}
                            />
                            <Tooltip content={<CustomTooltip />} cursor={false} />
                            <Area 
                                type="monotone" 
                                dataKey="NUMBER OF DONORS" 
                                stroke="#ff0000" 
                                strokeWidth={2}
                                fillOpacity={1}
                                fill="url(#colorDonors)"
                                dot={{ stroke: '#ff0000', strokeWidth: 2, fill: '#ffffff', r: 4 }}
                                activeDot={{ stroke: '#ff0000', strokeWidth: 2, fill: '#ffffff', r: 6 }}
                                isAnimationActive={true}
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>

                {/* Status/Metrics Section */}
                <div className="metrics-container">
                    {/* <div className="metric-box">
                        <div className="metric-icon-wrapper">
                            <BsGraphUp className="metric-icon" />
                        </div>
                        <div className="metric-details">
                            <span className="metric-label">{props.college ? "Total Branches" : "Total Colleges"}</span>
                            <span className="metric-value">{totalColleges}</span>
                        </div>
                    </div> */}
                    <div className="metric-divider"></div>
                    <div className="metric-box">
                        <div className="metric-icon-wrapper">
                            <BsPeopleFill className="metric-icon" />
                        </div>
                        <div className="metric-details">
                            <span className="metric-label">Total Donors</span>
                            <span className="metric-value">{totalDonors}</span>
                        </div>
                    </div>
                    <div className="metric-divider"></div>
                    <div className="metric-box">
                        <div className="metric-icon-wrapper">
                            <BsStarFill className="metric-icon" />
                        </div>
                        <div className="metric-details">
                            <span className="metric-label">Highest Donors</span>
                            <span className="metric-value">{highestCollege} ({highestCount})</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Chart1;